// src/components/PhotoField.jsx
//
// Champ photo réutilisable : « Prendre une photo » (caméra) + « Importer ».
// Produit toujours un File JPEG carré (600×600 max), donc le reste du code
// (form.photo instanceof File, FormData...) ne change pas.
//
// Utilisation :
//   <PhotoField
//     value={form.photo}                       // File | null (nouvelle photo)
//     onChange={file => handleFormChange("photo", file)}
//     currentUrl={selected?.photo ? getImageUrl(selected.photo) : null}
//   />
//
// - Caméra ARRIÈRE par défaut si elle existe, sinon repli automatique sur la
//   caméra frontale / webcam.
// - Bouton pour basculer entre les caméras (l'ancien flux est coupé avant
//   d'ouvrir le nouveau, pour éviter « caméra déjà utilisée »).
// - Sur PC en HTTPS : modal avec aperçu en direct, guide circulaire, reprise.
// - Pas de caméra / permission refusée / HTTP : repli sur l'appareil photo
//   natif du navigateur, ou sur l'import de fichier.

import { useEffect, useRef, useState } from "react";

const TAILLE = 600;          // côté max de la photo finale (px)
const QUALITE = 0.85;        // qualité JPEG
const MAX_IMPORT = 15 * 1024 * 1024; // fichier importé refusé au-delà (avant compression)

// ─── Utilitaires image ──────────────────────────────────────────────────────
const MAX_DECODE = 1400;          // largeur max de décodage (évite la saturation mémoire des mobiles)
const SEUIL_GROSSE_PHOTO = 1.5 * 1024 * 1024; // au-delà : décodage réduit

async function chargerImage(file) {
  if (window.createImageBitmap) {
    // Grosses photos de téléphone (12 à 50 Mpx) : on demande au navigateur de
    // les décoder DIRECTEMENT en petit, au lieu de charger l'image entière en mémoire.
    if (file.size > SEUIL_GROSSE_PHOTO) {
      try {
        return await createImageBitmap(file, {
          resizeWidth: MAX_DECODE,
          resizeQuality: "medium",
          imageOrientation: "from-image", // applique l'orientation EXIF
        });
      } catch {
        // option non supportée : essai suivant
      }
    }
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      try { return await createImageBitmap(file); } catch { /* repli ci-dessous */ }
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image illisible")); };
    img.src = url;
  });
}

// Recadre au carré (centré), redimensionne et exporte en File JPEG.
function carreVersFichier(source, nom) {
  const sw = source.videoWidth || source.naturalWidth || source.width;
  const sh = source.videoHeight || source.naturalHeight || source.height;
  const cote = Math.min(sw, sh);
  const sx = (sw - cote) / 2;
  const sy = (sh - cote) / 2;
  const sortie = Math.min(TAILLE, cote);

  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = sortie;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; // évite un fond noir pour les PNG transparents
  ctx.fillRect(0, 0, sortie, sortie);
  ctx.drawImage(source, sx, sy, cote, cote, 0, 0, sortie, sortie);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        // Libère la mémoire du canvas (important sur iPhone / Safari)
        canvas.width = 0;
        canvas.height = 0;
        if (blob) resolve(new File([blob], nom, { type: "image/jpeg" }));
        else reject(new Error("Export impossible"));
      },
      "image/jpeg",
      QUALITE
    );
  });
}

const cameraDisponible = () =>
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

const estTactile = () =>
  window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

// ─── Modal caméra ───────────────────────────────────────────────────────────
function CameraModal({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");        // caméra demandée (arrière par défaut)
  const [facingReel, setFacingReel] = useState("environment"); // caméra réellement obtenue
  const [aPlusieursCameras, setAPlusieursCameras] = useState(false);
  const [pret, setPret] = useState(false);
  const [erreur, setErreur] = useState("");
  const [capture, setCapture] = useState(null); // { file, url }
  const [traitement, setTraitement] = useState(false);

  const arreterFlux = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  // (Re)démarre le flux quand on change de caméra, sauf si une photo est figée.
  useEffect(() => {
    if (capture) return undefined;
    let annule = false;
    setPret(false);
    setErreur("");

    // Libère la caméra actuelle AVANT d'en demander une autre
    arreterFlux();
    if (videoRef.current) videoRef.current.srcObject = null;

    (async () => {
      try {
        // Petit délai : certains mobiles mettent un instant à libérer la caméra
        await new Promise(r => setTimeout(r, 150));
        if (annule) return;

        // « ideal » : arrière si elle existe, sinon repli automatique sur la frontale/webcam
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing }, width: { ideal: 1024 }, height: { ideal: 1024 } },
          audio: false,
        });
        if (annule) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        // Quelle caméra a-t-on vraiment obtenue ? (une webcam PC ne renseigne souvent pas facingMode)
        const reel = stream.getVideoTracks()[0]?.getSettings?.().facingMode;
        setFacingReel(reel || (estTactile() ? facing : "user"));

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setPret(true);
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          setAPlusieursCameras(devices.filter(d => d.kind === "videoinput").length > 1);
        } catch { /* non bloquant */ }
      } catch (err) {
        if (annule) return;
        if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
          setErreur("Accès à la caméra refusé. Autorisez-le dans les paramètres du navigateur, ou importez un fichier.");
        } else if (err?.name === "NotFoundError" || err?.name === "OverconstrainedError") {
          setErreur("Aucune caméra détectée sur cet appareil. Importez un fichier à la place.");
        } else if (err?.name === "NotReadableError") {
          setErreur("La caméra est utilisée par une autre application. Fermez-la puis réessayez.");
        } else {
          setErreur("Impossible d'ouvrir la caméra. Importez un fichier à la place.");
        }
      }
    })();

    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing, capture]);

  // Arrêt du flux (et libération de l'URL d'aperçu) à la fermeture du modal.
  useEffect(() => () => arreterFlux(), []);
  useEffect(() => () => { if (capture?.url) URL.revokeObjectURL(capture.url); }, [capture]);

  // Échap pour fermer
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const prendre = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setTraitement(true);
    try {
      const file = await carreVersFichier(v, `photo_${Date.now()}.jpg`);
      arreterFlux(); // on fige : la caméra s'éteint pendant la validation
      setCapture({ file, url: URL.createObjectURL(file) });
    } catch {
      setErreur("La capture a échoué. Réessayez.");
    } finally {
      setTraitement(false);
    }
  };

  const valider = () => {
    onCapture(capture.file);
    onClose();
  };

  // Miroir seulement pour la caméra frontale réellement utilisée
  const miroir = facingReel === "user";
  const btn = { cursor: "pointer" };

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Prendre une photo"
        style={{
          background: "var(--s1, #fff)", borderRadius: 12, width: "100%", maxWidth: 440,
          padding: 16, boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <strong style={{ fontSize: 15 }}>Photo de l'élève</strong>
          <button className="ibtn" style={{ border: "none" }} onClick={onClose} aria-label="Fermer">
            <i className="ti ti-x" />
          </button>
        </div>

        {erreur ? (
          <div style={{
            padding: 14, borderRadius: 8, fontSize: 13, color: "var(--rd, #dc3545)",
            background: "var(--s2, #f5f5f5)", lineHeight: 1.5,
          }}>
            <i className="ti ti-alert-triangle" /> {erreur}
          </div>
        ) : (
          <div style={{
            position: "relative", width: "100%", aspectRatio: "1 / 1", background: "#000",
            borderRadius: 10, overflow: "hidden",
          }}>
            {capture ? (
              <img
                src={capture.url}
                alt="Photo prise"
                style={{ width: "100%", height: "100%", objectFit: "cover", transform: miroir ? "scaleX(-1)" : "none" }}
              />
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: "100%", height: "100%", objectFit: "cover",
                    transform: miroir ? "scaleX(-1)" : "none", // effet miroir : plus naturel pour cadrer
                  }}
                />
                {/* Guide : le visage doit rester dans le cercle (les photos sont affichées en rond) */}
                <div style={{
                  position: "absolute", inset: 0, pointerEvents: "none",
                  background: "radial-gradient(circle at center, transparent 62%, rgba(0,0,0,0.45) 63%)",
                }} />
                <div style={{
                  position: "absolute", inset: "6%", borderRadius: "50%", pointerEvents: "none",
                  border: "2px dashed rgba(255,255,255,0.85)",
                }} />
                {!pret && (
                  <div style={{
                    position: "absolute", inset: 0, display: "flex", alignItems: "center",
                    justifyContent: "center", color: "#fff", fontSize: 13,
                  }}>
                    Ouverture de la caméra…
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="factions" style={{ marginTop: 14 }}>
          {capture ? (
            <>
              <button className="bsec" style={btn} onClick={() => setCapture(null)}>
                <i className="ti ti-refresh" /> Reprendre
              </button>
              <button className="bprim" style={btn} onClick={valider}>
                <i className="ti ti-check" /> Utiliser cette photo
              </button>
            </>
          ) : (
            <>
              <button className="bsec" style={btn} onClick={onClose}>Annuler</button>
              {!erreur && aPlusieursCameras && (
                <button
                  className="bsec"
                  style={btn}
                  onClick={() => setFacing(f => (f === "user" ? "environment" : "user"))}
                  title="Changer de caméra"
                  disabled={!pret}
                >
                  <i className="ti ti-camera-rotate" />
                </button>
              )}
              {!erreur && (
                <button className="bprim" style={btn} onClick={prendre} disabled={!pret || traitement}>
                  <i className="ti ti-camera" /> {traitement ? "..." : "Capturer"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Champ photo ────────────────────────────────────────────────────────────
export default function PhotoField({ value, onChange, currentUrl = null, label = "Photo" }) {
  const fileInputRef = useRef(null);
  const nativeCamRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [preview, setPreview] = useState(null);
  const [erreur, setErreur] = useState("");
  const [busy, setBusy] = useState(false);

  // Aperçu de la nouvelle photo
  useEffect(() => {
    if (!(value instanceof File)) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  // Fichier choisi (import ou appareil photo natif) → validation + compression
  const traiterFichier = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;
    setErreur("");

    if (!file.type.startsWith("image/")) {
      setErreur("Veuillez sélectionner une image valide.");
      return;
    }
    if (file.size > MAX_IMPORT) {
      setErreur("L'image est trop lourde (15 Mo maximum).");
      return;
    }

    setBusy(true);
    try {
      const img = await chargerImage(file);
      const nom = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      const compressee = await carreVersFichier(img, nom);
      img.close?.(); // libère le bitmap
      onChange(compressee);
    } catch {
      // Traitement impossible (mémoire de l'appareil...) : si le fichier est
      // raisonnable on l'envoie tel quel, sinon on demande de réduire la taille.
      if (file.size <= 5 * 1024 * 1024) {
        onChange(file);
      } else {
        setErreur("Photo trop lourde pour cet appareil. Réduisez la résolution de l'appareil photo ou choisissez une photo plus légère.");
      }
    } finally {
      setBusy(false);
    }
  };

  const ouvrirCamera = () => {
    setErreur("");
    // Caméra DANS la page (getUserMedia), y compris sur mobile.
    // Repli sur l'appareil photo natif si getUserMedia est indisponible
    // (site en HTTP, ancien navigateur).
    if (cameraDisponible()) {
      setShowCamera(true);
    } else {
      nativeCamRef.current?.click();
    }
  };

  const affichee = preview || currentUrl;

  return (
    <div className="fg">
      <label>{label}</label>

      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        {affichee ? (
          <img
            src={affichee}
            alt={preview ? "Nouvelle photo" : "Photo actuelle"}
            style={{
              width: 80, height: 80, borderRadius: "50%", objectFit: "cover",
              border: `2px solid ${preview ? "var(--pr)" : "var(--b)"}`,
            }}
          />
        ) : (
          <div style={{
            width: 80, height: 80, borderRadius: "50%", background: "var(--s2)",
            border: "2px dashed var(--b)", display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 28, color: "var(--h)",
          }}>
            <i className="ti ti-user" />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="bsec" onClick={ouvrirCamera} disabled={busy}>
              <i className="ti ti-camera" /> Prendre une photo
            </button>
            <button type="button" className="bsec" onClick={() => fileInputRef.current?.click()} disabled={busy}>
              <i className="ti ti-upload" /> Importer
            </button>
            {preview && (
              <button
                type="button"
                className="bsec"
                style={{ color: "var(--rd)" }}
                onClick={() => { setErreur(""); onChange(null); }}
                disabled={busy}
                title="Annuler la nouvelle photo"
              >
                <i className="ti ti-x" /> Retirer
              </button>
            )}
          </div>
          <span style={{ fontSize: 12, color: "var(--m)" }}>
            {busy
              ? "Traitement de l'image…"
              : preview
                ? "Nouvelle photo (sera enregistrée avec le formulaire)"
                : currentUrl
                  ? "Photo actuelle"
                  : "Aucune photo"}
          </span>
        </div>
      </div>

      {erreur && (
        <span style={{ color: "var(--rd)", fontSize: 12, marginTop: 4, display: "block" }}>{erreur}</span>
      )}

      {/* Import depuis les fichiers */}
      <input ref={fileInputRef} type="file" accept="image/*" onChange={traiterFichier} style={{ display: "none" }} />
      {/* Appareil photo natif (mobile) — caméra arrière */}
      <input
        ref={nativeCamRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={traiterFichier}
        style={{ display: "none" }}
      />

      {showCamera && (
        <CameraModal
          onCapture={file => onChange(file)}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}